import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { from, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FunctionUtils } from '@sharedcommon/util/functions.util';

@Component({
  selector: 'neq-select-autocomplete',
  templateUrl: './neq-select-autcomplete.component.html',
  // tslint:disable-next-line:use-host-property-decorator
  host: {'class': 'neq-select-autocomplete'},
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NeqSelectAutocompleteComponent),
      multi: true
    }
  ]
})
export class NeqSelectAutocompleteComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnChanges {

  searchSubject = new Subject<string>();
  @Input() dataSource: (searchTerm: string, page: number) => Promise<any>;
  /** Indicates if dataSource Promise returned a paginated results */
  @Input() paginatedResults: boolean = true;
  @Input() debounce = 500;
  @Input() bindLabel: string;
  @Input() defaultLabel = '';
  @Input() multiple = false;
  @Input() id = '';
  @Input() markFirst = true;
  @Input() clearable = true;
  @Input() appendTo = 'body';
  @Input() multipleCheckBox = false;
  @Input() redColor:boolean = false;
  @Input() validColor: boolean = false;
  /**
   * Here to force the loading spinner if needed
   */
  @Input() forcedLoading: boolean = false;
  /**
   * In case the fetch process is done by external process (not retrieved via dataSource input),
   * it allows you to passe the data in parameter
   */
  @Input() forcedData: any[];
  @Output() change = new EventEmitter();
  @Output() blur = new EventEmitter();
  @Output() clear = new EventEmitter();

  @ViewChild('select') select: NgSelectComponent;

  @ContentChild(TemplateRef) itemTemplate: TemplateRef<any>;

  
  loading = false;

  
  /** the fetched result or the forced result */
  data = [];

  /** Indicates if all results has been fetched */
  fetched = false;
  formControl = new FormControl();
  private searchedTerm: string;
  private page = 1;

  // FormControl functions
  private valueAccessorOnChange: (_: any) => void;
  private valueAccessorOnTouched: () => void;

  ngOnInit() {
    this.reset(true, false);
    this.searchSubject.pipe(debounceTime(this.debounce)).subscribe((search: string) => {
      this.reset(false, !this.multiple);
      this.searchedTerm = search;
      this.fetch();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.forcedData && this.forcedData) {
      this.data = this.filterForcedData();
    }
  }

  returnSearchSubject(): Subject<any> {
    /* If there is pagination then we need to send request otherwise we let the component
     * doing the filter by itself */
    return this.paginatedResults ? this.searchSubject : null;
  }

  ngAfterViewInit() {
    this.select.element.getElementsByClassName('ng-arrow')[0].innerHTML = '<i class="fas fa-chevron-down"></i>';
    this.select.markFirst = this.markFirst;
  }

  onScrollToEnd() {
    if (this.paginatedResults) {
      this.fetch();
    }
  }

  onChange(event: any) {
    if (this.valueAccessorOnChange) {
      this.valueAccessorOnChange(event);
    }
    this.change.emit(event);
  }

  onBlur(event: any) {
    this.blur.emit(event);
    if (this.valueAccessorOnTouched) {
      this.valueAccessorOnTouched();
    }
  }

  onClear() {
    this.clear.emit();
  }

  onClose() {
    if (!this.paginatedResults) {
      /* We reset the component (by sending API request) only if there is pagination only */
      this.reset(true, false);
    }
  }

  reset(search = true, resetValue = true) {
    if (resetValue && this.formControl.value === null) {
      this.select.writeValue(null);
      this.onChange(null);
    }
    this.fetched = false;
    this.page = 1;
    this.data = [];
    this.searchedTerm = null;
    if (search) {
      this.fetch();
    }
  }

  private fetch() {
    if (!this.fetched && this.dataSource) {
      this.loading = true;
      Promise.resolve(this.handleDataSourceAsObservable()).then((result: any) => {
        /* If the result lenght is equal to 0 OR if there is not any pagination
         * then no need to fetch one more time also for this case */
        this.fetched = result.length === 0 || !this.paginatedResults;
        this.data = this.data.concat(result);
        // Remove doubles
        this.data = this.data.filter((currentItem, index, self) =>
          index === self.findIndex((p) => JSON.stringify(p) === JSON.stringify(currentItem))
        );
        this.page++;
        this.loading = false;
      });
    } else if (this.forcedData) {
      // Filter with algorithm
      this.data = this.filterForcedData();
    }
  }

  filterForcedData(): any[] {
    // Filter with algorithm
    if (!this.searchedTerm || !this.forcedData) {
      return this.forcedData;
    } else {
      const lowerCaseSearchTerm = this.searchedTerm.toLocaleLowerCase();
      return this.forcedData
        .filter(item => {
          const valueToFilter = this.bindLabel ? item[this.bindLabel] : item;
          if (FunctionUtils.isDefined(valueToFilter)) {
            return ('' + valueToFilter).toLocaleLowerCase().includes(lowerCaseSearchTerm)
          } else {
            return false;
          }
        });
    }
  }

  /** convert the promise into observable to reconvert it into a promise 
   * to catch error and to rethrow to show the build in popin */
  private handleDataSourceAsObservable(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      from(this.dataSource(this.searchedTerm || '', this.page))
        .subscribe({
          next: (result) => resolve(result),
          error : (error) => {
            reject(error);
            throw error;
          }
        })
    })
  }

  isLoading(): boolean {
    return (this.dataSource && this.loading) || this.forcedLoading
  }

  writeValue(obj: any): void {
    if (this.select) {
      this.select.writeValue(obj);
    }
  }

  registerOnChange(fn: any): void {
    this.valueAccessorOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.valueAccessorOnTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.select) {
      this.select.setDisabledState(isDisabled);
    }
  }
}

